import React from 'react';
import { Badge } from '@/components/ui/badge';
import { describeRule } from '@/lib/formRules';

// Read-only rendering of a library master's schema_json (never editable — masters are immutable).
export function LibrarySchemaPreview({ fields = [], rules = [], engineVersion = null }) {
  if (!fields.length) return <p className="text-sm text-muted-foreground text-center py-6" data-testid="library-schema-empty">This master defines no fields.</p>;
  const ruleTargets = new Set(rules.flatMap(r => r?.then?.fields || []));
  const conditionalRequired = rules.filter(r => r?.then?.action === 'require');
  return (
    <div className="space-y-4">
      <ol className="space-y-3" data-testid="library-schema-preview">
        {fields.map((f, i) => (
          <li key={f.key || i} className="flex items-start gap-3 py-2 border-b border-dashed last:border-0" data-testid={`library-field-${f.key}`}>
            <span className="text-xs text-muted-foreground font-mono w-6 text-right shrink-0 pt-0.5">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">{f.label || f.key}</span>
                <Badge variant="outline" className="text-xs">{f.type}</Badge>
                {f.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                {ruleTargets.has(f.key) && <Badge variant="secondary" className="text-xs">Conditional</Badge>}
              </div>
              {f.help_text && <p className="text-xs text-muted-foreground mt-0.5">{f.help_text}</p>}
              {f.options?.length > 0 && <p className="text-xs text-muted-foreground mt-0.5">Options: {f.options.join(', ')}</p>}
              {f.type === 'signature' && <p className="text-xs text-muted-foreground mt-0.5">Typed-name acknowledgement only (not a drawn or certified signature).</p>}
            </div>
          </li>
        ))}
      </ol>
      {rules.length > 0 && (
        <section aria-labelledby="library-conditions-title" data-testid="library-conditions">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 id="library-conditions-title" className="text-sm font-semibold">Conditions ({rules.length})</h4>
            {engineVersion != null && <Badge variant="outline" className="text-xs" data-testid="library-conditions-engine">Rules engine v{engineVersion}</Badge>}
          </div>
          <p className="text-xs text-muted-foreground mt-1" data-testid="library-conditions-summary">
            {conditionalRequired.length > 0
              ? `This template contains conditionally required fields: ${conditionalRequired.length} condition${conditionalRequired.length === 1 ? '' : 's'} make${conditionalRequired.length === 1 ? 's' : ''} fields required when met.`
              : 'This template contains conditions that show or hide fields; none make fields required.'}
            {' '}Conditions become editable in your organisation's copy after import.
          </p>
          <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
            {rules.map(r => <li key={r.id} data-testid={`library-condition-${r.id}`}>{describeRule(r, fields)}</li>)}
          </ul>
        </section>
      )}
    </div>
  );
}
